import { Box, Flex, Text } from "@chakra-ui/react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Polygon,
  useMap,
} from "react-leaflet";
import { divIcon, LatLngLiteral } from "leaflet";
import { MapPinned } from "lucide-react";
import { useEffect, useMemo } from "react";
import { ConfigKey } from "enum/ConfigKey";
import { getConfig } from "config/app.config";
import { ILocationMenuStation } from "stores/data/operationRuleLocations/OperationRuleLocationModel";
import greenMarker from "assets/green-marker.svg";

// Component to fit map to bounds
function FitToBounds({
  bounds,
  point,
}: {
  bounds?: LatLngLiteral[];
  point?: { lat: number; lon: number };
}) {
  const map = useMap();

  useEffect(() => {
    if (bounds && bounds.length > 0) {
      const latLngs = bounds.map(
        (coord) => [coord.lat, coord.lng] as [number, number]
      );
      map.fitBounds(latLngs, { padding: [30, 30] });
    } else if (point) {
      map.setView([point.lat, point.lon], 10);
    }
  }, [bounds, point, map]);

  return null;
}

const pointMarkerIcon = divIcon({
  html: `<img src="${greenMarker}" alt="selected station" style="width: 25px; height: 30px;" />`,
  iconSize: [25, 30],
  iconAnchor: [12.5, 30],
  className: "",
});

interface MonitoredLocationPreviewProps {
  locationName: string;
  station?: ILocationMenuStation;
  geofence?: LatLngLiteral[];
  t: (key: string) => string;
}

export function MonitoredLocationPreview({
  locationName,
  station,
  geofence,
  t,
}: MonitoredLocationPreviewProps) {
  const mapTilerKey = getConfig(ConfigKey.REACT_APP_MAPTILER_KEY);

  // Point can be either a station OR a geofence with a single coordinate
  const isPointFromStation = !!station;
  const isPointFromGeofence = !station && !!geofence && geofence.length === 1;
  const isPoint = isPointFromStation || isPointFromGeofence;

  // Area is a geofence with more than one coordinate (polygon)
  const isArea = !station && !!geofence && geofence.length > 1;

  // Calculate center for initial map position
  const mapCenter = useMemo(() => {
    if (station) {
      return [station.lat, station.lon] as [number, number];
    }
    if (geofence && geofence.length > 0) {
      const lats = geofence.map((c) => c.lat);
      const lngs = geofence.map((c) => c.lng);
      return [
        (Math.min(...lats) + Math.max(...lats)) / 2,
        (Math.min(...lngs) + Math.max(...lngs)) / 2,
      ] as [number, number];
    }
    return [-23.5505, -46.6333] as [number, number]; // Default: São Paulo
  }, [station, geofence]);

  // Get coordinates display text
  const coordinatesText = useMemo(() => {
    if (station) {
      return `${station.lat.toFixed(4)}, ${station.lon.toFixed(4)}`;
    }
    if (geofence && geofence.length === 1) {
      // Single point geofence - show coordinates
      return `${geofence[0].lat.toFixed(4)}, ${geofence[0].lng.toFixed(4)}`;
    }
    if (geofence && geofence.length > 1) {
      return t("monitoredAreaPolygon");
    }
    return "";
  }, [station, geofence, t]);

  if (!isPoint && !isArea) {
    return null;
  }

  return (
    <Box borderWidth="1px" borderColor="#D4D4D8" borderRadius="8px" p={4}>
      {/* Header */}
      <Flex gap="12px" mb="16px">
        <MapPinned color="#086AA9" size="20px" />
        <Text
          fontWeight="400"
          fontSize="16px"
          lineHeight="24px"
          color="#086AA9"
        >
          {t("monitoredLocation")}
        </Text>
      </Flex>

      {/* Location info box */}
      <Box
        bg="#F4F4F5"
        border="1px solid"
        borderColor="#E4E4E7"
        borderRadius="8px"
        p={3}
        mb={4}
      >
        <Text fontWeight="500" fontSize="14px" color="#242E38">
          {locationName}
        </Text>
        <Text fontSize="12px" color="#71717A" mt={1}>
          {coordinatesText}
        </Text>
      </Box>

      {/* Map preview */}
      <Box
        position="relative"
        width="100%"
        height="200px"
        borderRadius="8px"
        overflow="hidden"
        aria-label={`${t("monitoredLocation")}: ${locationName}`}
        role="img"
      >
        <MapContainer
          center={mapCenter}
          zoom={10}
          style={{
            height: "200px",
            width: "100%",
            borderRadius: "8px",
          }}
          scrollWheelZoom={false}
          dragging={false}
          doubleClickZoom={false}
          zoomControl={false}
          touchZoom={false}
          boxZoom={false}
          keyboard={false}
        >
          <TileLayer
            attribution={
              '\u003ca href="https://www.maptiler.com/copyright/" target="_blank"\u003e\u0026copy; MapTiler\u003c/a\u003e \u003ca href="https://www.openstreetmap.org/copyright" target="_blank"\u003e\u0026copy; OpenStreetMap contributors\u003c/a\u003e'
            }
            tileSize={512}
            zoomOffset={-1}
            minZoom={1}
            crossOrigin={true}
            url={`https://api.maptiler.com/maps/019b6b50-0ba6-7f22-825f-03de06fca8a8/{z}/{x}/{y}.webp?key=${mapTilerKey}`}
            zIndex={700}
          />

          {isPoint && station && (
            <>
              <FitToBounds point={station} />
              <Marker
                position={[station.lat, station.lon]}
                icon={pointMarkerIcon}
              />
            </>
          )}

          {isPoint && isPointFromGeofence && geofence && (
            <>
              <FitToBounds
                point={{ lat: geofence[0].lat, lon: geofence[0].lng }}
              />
              <Marker
                position={[geofence[0].lat, geofence[0].lng]}
                icon={pointMarkerIcon}
              />
            </>
          )}

          {isArea && geofence && (
            <>
              <FitToBounds bounds={geofence} />
              <Polygon
                positions={geofence.map(
                  (coord) => [coord.lat, coord.lng] as [number, number]
                )}
                pathOptions={{
                  color: "#309E3A",
                  fillColor: "#309E3A",
                  fillOpacity: 0.2,
                  weight: 2,
                }}
              />
            </>
          )}
        </MapContainer>
      </Box>
    </Box>
  );
}
